// Users Manager Component - Admin & User role management with custom permissions
import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Loader2, Users, Shield, User, Edit2, X, Check, Eye, EyeOff, Key, Settings2, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UsersManager = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingPermissions, setEditingPermissions] = useState(null);
  const [saving, setSaving] = useState(false);
  const [availableTabs, setAvailableTabs] = useState([]);
  const [defaultPermissions, setDefaultPermissions] = useState([]);
  
  // New user form state
  const [newUser, setNewUser] = useState({
    email: '',
    name: '',
    role: 'user',
    auth_type: 'google',
    password: '',
    must_change_password: true,
    permissions: []
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showPermissionsInForm, setShowPermissionsInForm] = useState(false);

  useEffect(() => { 
    fetchUsers(); 
    fetchAvailableTabs();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users`, { credentials: 'include' });
      if (response.ok) { 
        const data = await response.json(); 
        setUsers(data || []); 
      }
    } catch (err) { console.error('Error fetching users:', err); }
    finally { setLoading(false); }
  };

  const fetchAvailableTabs = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/available-tabs`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setAvailableTabs(data.tabs || []);
        setDefaultPermissions(data.default_user_permissions || []);
        setNewUser(prev => ({ ...prev, permissions: data.default_user_permissions || [] }));
      }
    } catch (err) { console.error('Error fetching available tabs:', err); }
  };

  const handleAddUser = async () => {
    if (!newUser.email.trim()) return;
    if (newUser.auth_type === 'local' && !newUser.password) {
      toast.error('Mot de passe requis pour l\'authentification locale');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users`, {
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' }, 
        credentials: 'include', 
        body: JSON.stringify(newUser)
      });
      if (response.ok) { 
        toast.success('Utilisateur créé avec succès');
        setNewUser({ 
          email: '', name: '', role: 'user', auth_type: 'google', 
          password: '', must_change_password: true, permissions: defaultPermissions 
        });
        setShowAddForm(false);
        setShowPermissionsInForm(false);
        fetchUsers(); 
      } else { 
        const err = await response.json(); 
        toast.error(err.detail || 'Erreur'); 
      }
    } catch (err) { console.error('Error adding user:', err); }
    finally { setSaving(false); }
  };

  const handleUpdateUser = async (userId) => {
    if (!editingUser) return;
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editingUser)
      });
      if (response.ok) {
        toast.success('Utilisateur mis à jour');
        setEditingUser(null);
        fetchUsers();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch (err) { console.error('Error updating user:', err); }
    finally { setSaving(false); }
  };

  const handleUpdatePermissions = async (userId, permissions) => {
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}/permissions`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ permissions })
      });
      if (response.ok) {
        toast.success('Permissions mises à jour');
        setEditingPermissions(null);
        fetchUsers();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch (err) { console.error('Error updating permissions:', err); }
    finally { setSaving(false); }
  };

  const handleDeleteUser = async (userId) => {
    if (!userId) {
      console.error('User ID is undefined');
      return;
    }
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}`, { 
        method: 'DELETE', 
        credentials: 'include' 
      });
      if (response.ok) { 
        toast.success('Utilisateur supprimé');
        fetchUsers(); 
      }
      else { const err = await response.json(); toast.error(err.detail || 'Erreur'); }
    } catch (err) { console.error('Error deleting user:', err); }
  };

  const getRoleBadge = (role, isAdmin) => {
    const actualRole = role || (isAdmin ? 'admin' : 'user');
    if (actualRole === 'admin') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
          <Shield className="w-3 h-3" /> Admin
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
        <User className="w-3 h-3" /> Utilisateur
      </span>
    );
  };

  const getAuthBadge = (authType) => {
    if (authType === 'local') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
          <Key className="w-3 h-3" /> Local
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        </svg>
        Google
      </span>
    );
  };

  // Group tabs by category for display
  const getTabsByCategory = () => {
    const categories = {};
    const categoryLabels = {
      general: 'Général',
      pages: 'Pages',
      appearance: 'Apparence',
      services: 'Services',
      communications: 'Communications',
      admin: 'Administration'
    };
    
    availableTabs.forEach(tab => {
      const cat = tab.category || 'general';
      if (!categories[cat]) {
        categories[cat] = { label: categoryLabels[cat] || cat, tabs: [] };
      }
      categories[cat].tabs.push(tab);
    });
    return categories;
  };

  // Permission checkbox component
  const PermissionCheckbox = ({ tabId, label, permissions, setPermissions, disabled }) => (
    <label className={`flex items-center gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer ${disabled ? 'opacity-50' : ''}`}>
      <input
        type="checkbox"
        checked={permissions.includes(tabId)}
        onChange={(e) => {
          if (e.target.checked) {
            setPermissions([...permissions, tabId]);
          } else {
            setPermissions(permissions.filter(p => p !== tabId));
          }
        }}
        disabled={disabled}
        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
      />
      <span className="text-sm text-slate-700">{label}</span>
    </label>
  );

  // Permissions editor component
  const PermissionsEditor = ({ permissions, setPermissions, disabled = false }) => {
    const categories = getTabsByCategory();
    
    return (
      <div className="space-y-4 mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-slate-800 flex items-center gap-2">
            <Settings2 className="w-4 h-4" />
            Accès aux sections
          </h4>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPermissions(availableTabs.map(t => t.id))}
              className="text-xs text-blue-600 hover:underline"
              disabled={disabled}
            >
              Tout sélectionner
            </button>
            <span className="text-slate-300">|</span>
            <button
              type="button"
              onClick={() => setPermissions(defaultPermissions)}
              className="text-xs text-slate-600 hover:underline"
              disabled={disabled}
            >
              Par défaut
            </button>
            <span className="text-slate-300">|</span>
            <button
              type="button"
              onClick={() => setPermissions([])}
              className="text-xs text-red-600 hover:underline"
              disabled={disabled}
            >
              Aucun
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(categories).map(([catKey, { label, tabs }]) => (
            <div key={catKey} className="bg-white rounded-lg p-3 border border-slate-200">
              <h5 className="text-xs font-semibold text-slate-500 uppercase mb-2">{label}</h5>
              <div className="space-y-1">
                {tabs.map(tab => (
                  <PermissionCheckbox
                    key={tab.id}
                    tabId={tab.id}
                    label={tab.label}
                    permissions={permissions}
                    setPermissions={setPermissions}
                    disabled={disabled}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  // Separate admins and users
  const admins = users.filter(u => u.role === 'admin' || u.is_admin);
  const regularUsers = users.filter(u => u.role === 'user' && !u.is_admin);

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-light text-slate-800">Gestion des Utilisateurs</h2>
          <p className="text-sm text-slate-500 mt-1">Gérez les administrateurs et utilisateurs du panneau</p>
        </div>
        <Button onClick={() => setShowAddForm(true)} data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-2" /> Ajouter
        </Button>
      </div>

      {/* Add User Form */}
      {showAddForm && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-medium text-slate-800">Nouvel utilisateur</h3>
            <button onClick={() => { setShowAddForm(false); setShowPermissionsInForm(false); }} className="text-slate-400 hover:text-slate-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
              <input 
                type="email" 
                value={newUser.email} 
                onChange={(e) => setNewUser({...newUser, email: e.target.value})} 
                placeholder="email@exemple.com" 
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="new-user-email"
              />
            </div>
            
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nom</label>
              <input 
                type="text" 
                value={newUser.name} 
                onChange={(e) => setNewUser({...newUser, name: e.target.value})} 
                placeholder="Nom complet" 
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="new-user-name"
              />
            </div>
            
            {/* Role */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Rôle *</label>
              <select 
                value={newUser.role} 
                onChange={(e) => {
                  const isAdmin = e.target.value === 'admin';
                  setNewUser({
                    ...newUser, 
                    role: e.target.value,
                    permissions: isAdmin ? [] : defaultPermissions
                  });
                  if (isAdmin) setShowPermissionsInForm(false);
                }}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="new-user-role"
              >
                <option value="user">Utilisateur (accès personnalisable)</option>
                <option value="admin">Administrateur (accès complet)</option>
              </select>
            </div>
            
            {/* Auth Type */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Type de connexion *</label>
              <select 
                value={newUser.auth_type} 
                onChange={(e) => setNewUser({...newUser, auth_type: e.target.value, password: ''})}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="new-user-auth-type"
              >
                <option value="google">Google</option>
                <option value="local">Email / Mot de passe</option>
              </select>
            </div>
            
            {/* Password (only for local auth) */}
            {newUser.auth_type === 'local' && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Mot de passe *</label>
                <div className="relative">
                  <input 
                    type={showPassword ? 'text' : 'password'} 
                    value={newUser.password} 
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})} 
                    placeholder="Mot de passe" 
                    className="w-full px-3 py-2 pr-10 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    data-testid="new-user-password"
                  />
                  <button 
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            )}
            
            {/* Force change password checkbox (only for local auth) */}
            {newUser.auth_type === 'local' && (
              <div className="md:col-span-2 flex items-center gap-2">
                <input 
                  type="checkbox"
                  id="must_change_password"
                  checked={newUser.must_change_password !== false}
                  onChange={(e) => setNewUser({...newUser, must_change_password: e.target.checked})}
                  className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="must_change_password" className="text-sm text-slate-700">
                  Forcer le changement de mot de passe à la première connexion
                </label>
              </div>
            )}
          </div>
          
          {/* Role Description */}
          <div className="mt-4 p-3 bg-slate-50 rounded-lg">
            {newUser.role === 'admin' ? (
              <div className="text-sm text-slate-600">
                <span className="font-medium text-purple-700">Administrateur :</span> Accès complet à toutes les fonctionnalités du panneau d'administration.
              </div>
            ) : (
              <div className="text-sm text-slate-600">
                <span className="font-medium text-blue-700">Utilisateur :</span> Accès limité aux sections que vous sélectionnez ci-dessous.
                <button
                  type="button"
                  onClick={() => setShowPermissionsInForm(!showPermissionsInForm)}
                  className="ml-2 text-blue-600 hover:underline inline-flex items-center gap-1"
                >
                  Personnaliser les accès
                  {showPermissionsInForm ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
              </div>
            )}
          </div>
          
          {/* Permissions for new user (only if role is user) */}
          {newUser.role === 'user' && showPermissionsInForm && (
            <PermissionsEditor
              permissions={newUser.permissions}
              setPermissions={(perms) => setNewUser({...newUser, permissions: perms})}
            />
          )}
          
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => { setShowAddForm(false); setShowPermissionsInForm(false); }}>Annuler</Button>
            <Button onClick={handleAddUser} disabled={saving || !newUser.email.trim()} data-testid="save-new-user-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Créer
            </Button>
          </div>
        </div>
      )}

      {/* Admins List */}
      <div>
        <h3 className="text-lg font-medium text-slate-800 mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-purple-600" />
          Administrateurs ({admins.length})
        </h3>
        <div className="space-y-3">
          {admins.map((user) => {
            const id = user.user_id || user.id;
            const isEditing = editingUser?.user_id === id;
            
            return (
              <div key={id} className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-sm transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-12 h-12 rounded-full" />
                    ) : (
                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                        <Shield className="w-6 h-6 text-purple-600" />
                      </div>
                    )}
                    <div>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editingUser.name}
                          onChange={(e) => setEditingUser({...editingUser, name: e.target.value})}
                          className="font-medium text-slate-800 border border-slate-300 rounded px-2 py-1"
                        />
                      ) : (
                        <h3 className="font-medium text-slate-800">{user.name || 'Administrateur'}</h3>
                      )}
                      <p className="text-sm text-slate-500">{user.email}</p>
                      <div className="flex gap-2 mt-1">
                        {getRoleBadge(user.role, user.is_admin)}
                        {getAuthBadge(user.auth_type)}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {isEditing ? (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => setEditingUser(null)}>
                          <X className="w-4 h-4 text-slate-500" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleUpdateUser(id)}>
                          <Check className="w-4 h-4 text-green-500" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => setEditingUser({...user, user_id: id})} title="Modifier">
                          <Edit2 className="w-4 h-4 text-slate-500" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(id)} title="Supprimer">
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {admins.length === 0 && (
            <p className="text-center text-slate-500 py-8 bg-slate-50 rounded-xl">Aucun administrateur</p>
          )}
        </div>
      </div>

      {/* Regular Users List */}
      <div>
        <h3 className="text-lg font-medium text-slate-800 mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-600" />
          Utilisateurs ({regularUsers.length})
        </h3>
        <div className="space-y-3">
          {regularUsers.map((user) => {
            const id = user.user_id || user.id;
            const isEditing = editingUser?.user_id === id;
            const isEditingPerms = editingPermissions?.user_id === id;
            const userPermissions = user.permissions || defaultPermissions;
            
            return (
              <div key={id} className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-sm transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-12 h-12 rounded-full" />
                    ) : (
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-blue-600" />
                      </div>
                    )}
                    <div>
                      {isEditing ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={editingUser.name}
                            onChange={(e) => setEditingUser({...editingUser, name: e.target.value})}
                            className="font-medium text-slate-800 border border-slate-300 rounded px-2 py-1 w-full"
                            placeholder="Nom"
                          />
                          <select
                            value={editingUser.role || 'user'}
                            onChange={(e) => setEditingUser({...editingUser, role: e.target.value})}
                            className="text-sm border border-slate-300 rounded px-2 py-1"
                          >
                            <option value="user">Utilisateur</option>
                            <option value="admin">Administrateur</option>
                          </select>
                        </div>
                      ) : (
                        <>
                          <h3 className="font-medium text-slate-800">{user.name || 'Utilisateur'}</h3>
                          <p className="text-sm text-slate-500">{user.email}</p>
                        </>
                      )}
                      {!isEditing && (
                        <div className="flex flex-wrap gap-2 mt-1">
                          {getRoleBadge(user.role, user.is_admin)}
                          {getAuthBadge(user.auth_type)}
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 text-xs rounded-full">
                            <Settings2 className="w-3 h-3" />
                            {userPermissions.length} accès
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {isEditing ? (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => setEditingUser(null)}>
                          <X className="w-4 h-4 text-slate-500" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleUpdateUser(id)}>
                          <Check className="w-4 h-4 text-green-500" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => {
                            if (isEditingPerms) {
                              setEditingPermissions(null);
                            } else {
                              setEditingPermissions({ ...user, user_id: id, permissions: userPermissions });
                            }
                          }} 
                          title="Gérer les accès"
                          className={isEditingPerms ? 'bg-blue-100' : ''}
                        >
                          <Settings2 className="w-4 h-4 text-blue-500" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setEditingUser({...user, user_id: id})} title="Modifier">
                          <Edit2 className="w-4 h-4 text-slate-500" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(id)} title="Supprimer">
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Permissions editor for this user */}
                {isEditingPerms && (
                  <div className="mt-4 border-t border-slate-200 pt-4">
                    <PermissionsEditor
                      permissions={editingPermissions.permissions}
                      setPermissions={(perms) => setEditingPermissions({...editingPermissions, permissions: perms})}
                    />
                    <div className="flex justify-end gap-2 mt-4">
                      <Button variant="outline" size="sm" onClick={() => setEditingPermissions(null)}>
                        Annuler
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={() => handleUpdatePermissions(id, editingPermissions.permissions)}
                        disabled={saving}
                      >
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                        Enregistrer les accès
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {regularUsers.length === 0 && (
            <p className="text-center text-slate-500 py-8 bg-slate-50 rounded-xl">Aucun utilisateur</p>
          )}
        </div>
      </div>

      {users.length === 0 && !showAddForm && (
        <div className="text-center py-12 text-slate-500">
          <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p>Aucun utilisateur configuré.</p>
          <Button onClick={() => setShowAddForm(true)} className="mt-4">
            <Plus className="w-4 h-4 mr-2" /> Ajouter un utilisateur
          </Button>
        </div>
      )}
    </div>
  );
};

export default UsersManager;
